"use client"

import * as React from "react"
import {
  X,
  Search,
  Check,
  CheckCheck,
  Building2,
  FolderKanban,
  Settings,
  Download,
  AlertCircle,
  Loader2,
  Layers,
  Sparkles
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { AppIcon } from "@/components/desk/AppIcon"
import { App, Language, Workspace } from "@/types/workspace"

interface AppWorkspacesModalProps {
  isOpen: boolean
  onClose: () => void
  app: App | null
  mode: "install" | "configure"
  workspaces: Workspace[]
  currentWorkspace?: Workspace | null
  onConfirm: (workspaceIds: string[]) => Promise<void>
  t?: Record<string, string>
  lang?: Language
}

export function AppWorkspacesModal({
  isOpen,
  onClose,
  app,
  mode,
  workspaces = [],
  currentWorkspace,
  onConfirm,
  t = {},
  lang = "pt",
}: AppWorkspacesModalProps) {
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])
  const [initialInstalledIds, setInitialInstalledIds] = React.useState<string[]>([])
  const [nonAdminInstalledIds, setNonAdminInstalledIds] = React.useState<string[]>([])
  const [searchQuery, setSearchQuery] = React.useState("")
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  // Filter only active workspaces ("Workspaces (ativos) em que o usuário é Admin")
  const activeWorkspaces = React.useMemo(() => {
    return workspaces.filter((w) => w.status !== false)
  }, [workspaces])

  // Filter by search query
  const filteredWorkspaces = React.useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    if (!q) return activeWorkspaces
    return activeWorkspaces.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        (w.slug && w.slug.toLowerCase().includes(q))
    )
  }, [activeWorkspaces, searchQuery])

  // Initialize selected workspaces when modal opens
  React.useEffect(() => {
    if (!isOpen || !app) {
      setSelectedIds([])
      setInitialInstalledIds([])
      setNonAdminInstalledIds([])
      setSearchQuery("")
      return
    }

    const adminWsIdSet = new Set(activeWorkspaces.map((w) => w.id))

    // Find initial installed workspaces from local state among admin workspaces
    const localInstalled = activeWorkspaces
      .filter((ws) =>
        ws.installedApps?.some(
          (ia: any) =>
            ia.status !== "inactive" &&
            (ia.app?.id === app.id ||
              (Boolean(app.slug) && ia.app?.slug === app.slug) ||
              ia.id === app.id ||
              ia.appId === app.id ||
              ia.id === `installed-${app.id}`)
        )
      )
      .map((ws) => ws.id)

    if (mode === "install") {
      const preselected = new Set<string>(localInstalled)
      if (currentWorkspace?.id && adminWsIdSet.has(currentWorkspace.id)) {
        preselected.add(currentWorkspace.id)
      } else if (preselected.size === 0 && activeWorkspaces.length === 1) {
        preselected.add(activeWorkspaces[0].id)
      }
      setSelectedIds(Array.from(preselected))
      setInitialInstalledIds(localInstalled)
    } else {
      setSelectedIds(localInstalled)
      setInitialInstalledIds(localInstalled)
    }

    // Query GraphQL appWorkspaces for real-time consistency
    const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null
    if (token) {
      if (mode === "configure") setIsLoading(true)
      fetch("/api/graphql", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: `
            query GetAppWorkspaces($appId: ID!) {
              appWorkspaces(appId: $appId)
            }
          `,
          variables: { appId: app.id },
        }),
      })
        .then((res) => res.json())
        .then((resData) => {
          if (resData.data && Array.isArray(resData.data.appWorkspaces)) {
            const allServerInstalledIds: string[] = resData.data.appWorkspaces
            const adminServerInstalled = allServerInstalledIds.filter((id) => adminWsIdSet.has(id))
            const nonAdminServerInstalled = allServerInstalledIds.filter((id) => !adminWsIdSet.has(id))
            setNonAdminInstalledIds(nonAdminServerInstalled)
            setInitialInstalledIds(adminServerInstalled)
            if (mode === "install") {
              const merged = new Set<string>(adminServerInstalled)
              if (currentWorkspace?.id && adminWsIdSet.has(currentWorkspace.id)) {
                merged.add(currentWorkspace.id)
              }
              setSelectedIds(Array.from(merged))
            } else {
              setSelectedIds(adminServerInstalled)
            }
          }
        })
        .catch((err) => {
          console.error("Erro ao buscar appWorkspaces:", err)
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      setIsLoading(false)
    }
  }, [isOpen, app, mode, activeWorkspaces, currentWorkspace])

  if (!isOpen || !app) return null

  const handleToggleWorkspace = (wsId: string) => {
    setSelectedIds((prev) =>
      prev.includes(wsId) ? prev.filter((id) => id !== wsId) : [...prev, wsId]
    )
  }

  const handleSelectAll = () => {
    const allFilteredIds = filteredWorkspaces.map((w) => w.id)
    setSelectedIds((prev) => Array.from(new Set([...prev, ...allFilteredIds])))
  }

  const handleDeselectAll = () => {
    const filteredIdSet = new Set(filteredWorkspaces.map((w) => w.id))
    setSelectedIds((prev) => prev.filter((id) => !filteredIdSet.has(id)))
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const finalWorkspaceIds = Array.from(new Set([...selectedIds, ...nonAdminInstalledIds]))
      await onConfirm(finalWorkspaceIds)
      onClose()
    } catch (e) {
      console.error("Erro ao salvar configuração do app:", e)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isInstallMode = mode === "install"
  const canSubmit = !isInstallMode || selectedIds.length > 0

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 font-sans animate-in fade-in duration-200">
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-5 border-b border-zinc-150 dark:border-zinc-800/80 flex items-center justify-between gap-4 shrink-0 bg-zinc-50/60 dark:bg-zinc-950/40">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="size-11 rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-1 flex items-center justify-center shrink-0 shadow-xs">
              <AppIcon
                iconUrl={app.iconUrl}
                iframeUrl={app.iframeUrl}
                name={app.name}
                size="medium"
              />
            </div>
            <div className="min-w-0 space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-100 truncate">
                  {isInstallMode ? `Instalar ${app.name}` : `Configurar ${app.name}`}
                </h3>
              </div>
              <p className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">
                {isInstallMode
                  ? "Selecione os ambientes onde o app será instalado e ativado"
                  : "Gerencie os ambientes que têm este aplicativo ativo"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="size-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 space-y-3.5 flex-1 overflow-y-auto">
          {/* Search & Actions Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-zinc-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Filtrar ambientes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-7 py-1.5 text-xs rounded-xl border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-white placeholder:text-zinc-400 focus:outline-none focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] transition-all"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                >
                  <X className="size-3" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-[11px] font-semibold px-2 py-1 rounded-lg text-[#004EB1] dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors flex items-center gap-1 cursor-pointer"
              >
                <CheckCheck className="size-3" />
                <span>Todos</span>
              </button>
              <span className="text-zinc-300 dark:text-zinc-700">|</span>
              <button
                type="button"
                onClick={handleDeselectAll}
                className="text-[11px] font-semibold px-2 py-1 rounded-lg text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                Desmarcar
              </button>
            </div>
          </div>

          {/* Selection counter */}
          <div className="flex items-center justify-between text-[11px] text-zinc-500 dark:text-zinc-400 px-0.5">
            <span>Ambientes Ativos</span>
            <span className="font-semibold text-zinc-700 dark:text-zinc-300">
              {selectedIds.length} de {activeWorkspaces.length} selecionados
            </span>
          </div>

          {/* Workspaces List */}
          {isLoading ? (
            <div className="py-12 flex flex-col items-center justify-center gap-2 text-zinc-400 dark:text-zinc-500">
              <Loader2 className="size-6 animate-spin text-[#004EB1] dark:text-blue-400" />
              <span className="text-xs">Carregando ambientes...</span>
            </div>
          ) : activeWorkspaces.length === 0 ? (
            <div className="py-10 text-center rounded-xl border border-dashed border-zinc-200 dark:border-zinc-800 p-4 space-y-1">
              <Building2 className="size-7 mx-auto text-zinc-300 dark:text-zinc-600" />
              <p className="text-xs font-semibold text-zinc-600 dark:text-zinc-400">
                Nenhum ambiente ativo encontrado
              </p>
              <p className="text-[11px] text-zinc-400">
                Esta conta não possui ambientes ativos para instalar aplicativos.
              </p>
            </div>
          ) : filteredWorkspaces.length === 0 ? (
            <div className="py-8 text-center text-xs text-zinc-400 italic">
              Nenhum ambiente corresponde à busca "{searchQuery}".
            </div>
          ) : (
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1 scrollbar-thin">
              {filteredWorkspaces.map((ws) => {
                const isSelected = selectedIds.includes(ws.id)
                const isCurrent = currentWorkspace?.id === ws.id
                const wasPreviouslyInstalled = initialInstalledIds.includes(ws.id)

                return (
                  <div
                    key={ws.id}
                    onClick={() => handleToggleWorkspace(ws.id)}
                    className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${isSelected
                      ? "bg-blue-50/50 dark:bg-blue-950/20 border-[#004EB1]/40 dark:border-blue-500/40 shadow-xs"
                      : "bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50/50 dark:hover:bg-zinc-800/40"
                      }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {/* Checkbox circle */}
                      <div
                        className={`size-5 rounded-md flex items-center justify-center border transition-all shrink-0 ${isSelected
                          ? "bg-[#004EB1] border-[#004EB1] text-white"
                          : "border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800"
                          }`}
                      >
                        {isSelected && <Check className="size-3.5 stroke-[3]" />}
                      </div>

                      <div className="size-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 border border-zinc-200/60 dark:border-zinc-700 flex items-center justify-center shrink-0 text-zinc-600 dark:text-zinc-300">
                        <FolderKanban className="size-4" />
                      </div>

                      <div className="min-w-0 space-y-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-zinc-900 dark:text-zinc-100 truncate">
                            {ws.name}
                          </span>
                          {isCurrent && (
                            <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700">
                              Atual
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400 font-mono truncate">
                          /{ws.slug || ws.id.slice(0, 8)}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Validation warning */}
          {isInstallMode && selectedIds.length === 0 && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 text-[11px] text-amber-800 dark:text-amber-300">
              <AlertCircle className="size-4 shrink-0" />
              <span>Selecione ao menos um ambiente para instalar o aplicativo.</span>
            </div>
          )}

          {!isInstallMode && selectedIds.length === 0 && (
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 text-[11px] text-red-800 dark:text-red-300">
              <AlertCircle className="size-4 shrink-0" />
              <span>
                Desmarcar todos os ambientes removerá o aplicativo da sua conta.
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-zinc-50 dark:bg-zinc-950/60 border-t border-zinc-150 dark:border-zinc-800 flex items-center justify-end gap-3 shrink-0">
          <Button
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            className={`text-xs h-8 px-4 font-semibold cursor-pointer font-sans transition-all flex items-center gap-2 ${isInstallMode
              ? "bg-[#004EB1] hover:bg-[#003d8d] text-white"
              : "bg-zinc-900 hover:bg-black dark:bg-zinc-100 dark:hover:bg-white text-white dark:text-zinc-900"
              }`}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                <span>Salvando...</span>
              </>
            ) : isInstallMode ? (
              <>
                <Download className="size-3.5" />
                <span>
                  Instalar
                </span>
              </>
            ) : (
              <>
                <Check className="size-3.5" />
                <span>
                  Salvar
                </span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
